import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress,
  Grid, InputAdornment, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Stack, useTheme, useMediaQuery,
  Select, MenuItem, FormControl
} from '@mui/material';
import { Refresh as RefreshIcon, Save as SaveIcon, Search as SearchIcon, ArrowBack } from '@mui/icons-material';
import { reportCardAPI, academicAPI, studentAPI } from '../../services/api';
import ModulePageHeader from '../common/ModulePageHeader';
import ResponsiveRecordList, { EmptyRecordsState } from '../common/ResponsiveRecordList';
import TablePagination from '../common/TablePagination';
import { useAppSnackbar } from '../common/TableDesign';

const autoGenerateGeneralComments = async (studentList, conducts, categories, termObj, academicYear) => {
  if (!termObj?.id) return;
  let templates;
  try {
    const res = await reportCardAPI.getCommentTemplates({ comment_type: 'General' });
    templates = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
  } catch { return; }
  if (!templates.length) return;

  await Promise.allSettled(studentList.map(async (student) => {
    const studentConducts = conducts[student.id] || {};
    // Find first category where student has a rating, match template by category+rating
    for (const cat of categories) {
      const cell = studentConducts[cat.id];
      if (!cell?.rating_id) continue;
      const match = templates.find((t) =>
        Number(t.conduct_category_id) === Number(cat.id) &&
        Number(t.conduct_rating_id) === Number(cell.rating_id)
      );
      if (match) {
        await reportCardAPI.autoGenerateGeneralComment({
          student_id: Number(student.id),
          term_id: Number(termObj.id),
          academic_year: Number(termObj.academic_year || termObj.year || academicYear),
          class_teacher_comment: match.comment_text,
        });
        break;
      }
    }
  }));
};

const extractCollection = (r) => {
  const p = r?.data !== undefined ? r.data : r;
  if (Array.isArray(p)) return p;
  if (Array.isArray(p?.data)) return p.data;
  if (Array.isArray(p?.data?.data)) return p.data.data;
  return [];
};
const getErrorMessage = (e, fallback = 'Operation failed') =>
  e?.response?.data?.message || e?.response?.data?.error || fallback;
const getStudentName = (s) =>
  s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || `Student #${s?.id}`;
const getRatingLabel = (r) => r ? `${r.rating_name} (${r.rating_value})` : '';

export default function BulkConductEntry({ onBack }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();

  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedTerm, setSelectedTerm] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear());

  const [conducts, setConducts] = useState({});
  const [savedConducts, setSavedConducts] = useState({});

  const [loadingFilters, setLoadingFilters] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingStudent, setSavingStudent] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const activeRatings = useMemo(() => ratings.filter((r) => r.is_active !== false), [ratings]);
  const activeCategories = useMemo(() => categories.filter((c) => c.is_active !== false), [categories]);

  const fetchFilters = useCallback(async () => {
    setLoadingFilters(true);
    try {
      const [termRes, classRes, catRes, ratRes] = await Promise.all([
        academicAPI.getTerms(), academicAPI.getAllClasses(),
        reportCardAPI.getConductCategories(), reportCardAPI.getConductRatings(),
      ]);
      setTerms(extractCollection(termRes));
      setClasses(extractCollection(classRes));
      setCategories(extractCollection(catRes));
      setRatings(extractCollection(ratRes));
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to load filters'), 'error');
    } finally {
      setLoadingFilters(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchFilters(); }, [fetchFilters]);

  const fetchStudentsAndConducts = useCallback(async () => {
    if (!selectedTerm?.id || !selectedClass?.id) return;
    setLoadingStudents(true);
    try {
      const [stuRes, conductRes] = await Promise.all([
        studentAPI.getActive({ class_id: selectedClass.id, per_page: 1000 }),
        reportCardAPI.getStudentConduct({ term_id: selectedTerm.id, academic_year: academicYear, per_page: 1000 }),
      ]);
      const studentList = extractCollection(stuRes);
      const conductList = extractCollection(conductRes);

      const next = {};
      studentList.forEach((s) => { next[s.id] = {}; });
      conductList.forEach((c) => {
        const sid = c.student_id || c.student?.id;
        const cid = c.conduct_category_id || c.category?.id;
        if (sid && cid) {
          if (!next[sid]) next[sid] = {};
          next[sid][cid] = { rating_id: c.conduct_rating_id || c.rating?.id, remarks: c.remarks || '', saved_id: c.id };
        }
      });

      setStudents(studentList);
      setConducts(next);
      setSavedConducts(JSON.parse(JSON.stringify(next)));
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to load students'), 'error');
    } finally {
      setLoadingStudents(false);
    }
  }, [selectedTerm, selectedClass, academicYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedTerm && selectedClass) fetchStudentsAndConducts();
    else { setStudents([]); setConducts({}); setSavedConducts({}); }
  }, [fetchStudentsAndConducts, selectedTerm, selectedClass]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) =>
      getStudentName(s).toLowerCase().includes(q) ||
      (s.registration_no || '').toLowerCase().includes(q)
    );
  }, [students, search]);

  const pagination = useMemo(() => {
    const total = filteredStudents.length;
    const last_page = Math.max(1, Math.ceil(total / perPage));
    const current_page = Math.min(page, last_page);
    return { total, per_page: perPage, current_page, last_page };
  }, [filteredStudents.length, page, perPage]);

  const pagedStudents = useMemo(() => {
    const start = (pagination.current_page - 1) * perPage;
    return filteredStudents.slice(start, start + perPage);
  }, [filteredStudents, pagination.current_page, perPage]);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(conducts) !== JSON.stringify(savedConducts);
  }, [conducts, savedConducts]);

  const setCell = (studentId, categoryId, field, value) => {
    setConducts((prev) => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [categoryId]: { ...(prev[studentId]?.[categoryId] || {}), [field]: value },
      },
    }));
  };

  const buildPayload = (studentList, currentConducts, cats) => {
    const payload = [];
    studentList.forEach((s) => {
      cats.forEach((cat) => {
        const cell = currentConducts[s.id]?.[cat.id];
        if (cell?.rating_id) payload.push({
          student_id: s.id,
          conduct_category_id: cat.id,
          conduct_rating_id: cell.rating_id,
          remarks: cell.remarks || '',
        });
      });
    });
    return payload;
  };

  const doSave = async (studentList, setLoadingFn) => {
    const payload = buildPayload(studentList, conducts, activeCategories);
    if (!payload.length) { showSnackbar('No conduct entries to save', 'warning'); return; }
    setLoadingFn(true);
    try {
      await reportCardAPI.bulkCreateStudentConduct({ term_id: selectedTerm.id, academic_year: academicYear, conducts: payload });
      showSnackbar('Saved successfully');
      setSavedConducts(JSON.parse(JSON.stringify(conducts)));
      fetchStudentsAndConducts();
      autoGenerateGeneralComments(studentList, conducts, activeCategories, selectedTerm, academicYear);
    } catch (e) {
      showSnackbar(getErrorMessage(e, 'Failed to save conduct'), 'error');
    } finally {
      setLoadingFn(false);
    }
  };

  const handleSaveAll = () => {
    if (!selectedTerm?.id || !selectedClass?.id) { showSnackbar('Select term and class first', 'error'); return; }
    doSave(students, setSaving);
  };

  const handleSaveStudent = (student) => {
    doSave([student], (v) => setSavingStudent(v ? student.id : null));
  };

  const savedCount = useMemo(() => {
    let count = 0;
    Object.values(savedConducts).forEach((cats) => {
      Object.values(cats).forEach((c) => { if (c?.saved_id) count++; });
    });
    return count;
  }, [savedConducts]);

  const renderRatingSelect = (student, category) => {
    const cell = conducts[student.id]?.[category.id] || {};
    const isSaved = !!savedConducts[student.id]?.[category.id]?.saved_id;
    return (
      <FormControl size="small" fullWidth sx={{ minWidth: 130 }}>
        <Select
          value={cell.rating_id || ''}
          onChange={(e) => setCell(student.id, category.id, 'rating_id', e.target.value || null)}
          disabled={saving || savingStudent === student.id}
          displayEmpty
          sx={{
            fontSize: '0.8rem',
            bgcolor: isSaved ? 'success.50' : 'white',
            '& .MuiSelect-select': { py: 0.75 },
          }}
        >
          <MenuItem value=""><em>—</em></MenuItem>
          {activeRatings.map((r) => (
            <MenuItem key={r.id} value={r.id} sx={{ fontSize: '0.8rem' }}>
              {getRatingLabel(r)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  return (
    <Box>
      <ModulePageHeader
        title="Bulk Conduct Entry"
        subtitle="Select term and class, then assign ratings per student per category"
        actions={[
          onBack && <Button key="back" variant="outlined" startIcon={<ArrowBack />} onClick={onBack}>Back</Button>,
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchFilters} disabled={loadingFilters || saving}>Refresh</Button>,
        ].filter(Boolean)}
      />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <TextField fullWidth size="small" type="number" label="Academic Year"
                value={academicYear} onChange={(e) => setAcademicYear(Number(e.target.value))}
                inputProps={{ min: 2000, max: 2100 }} />
            </Grid>
            <Grid item xs={6} sm={4} md={3}>
              <Autocomplete fullWidth options={terms} value={selectedTerm}
                onChange={(_, v) => setSelectedTerm(v)}
                getOptionLabel={(t) => t?.name || t?.term_name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Term" size="small" />} />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete fullWidth options={classes} value={selectedClass}
                onChange={(_, v) => setSelectedClass(v)}
                getOptionLabel={(c) => c?.name || c?.class_name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={loadingFilters}
                renderInput={(p) => <TextField {...p} label="Class" size="small" />} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {selectedTerm && selectedClass && (
        <Card>
          <CardContent>
            <Box mb={2}>
              <TextField fullWidth size="small" placeholder="Search student..."
                value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                sx={{ mb: 1.5 }} />
              <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap" alignItems="center">
                <Chip label={`Students: ${filteredStudents.length}`} size="small" />
                <Chip label={`Saved: ${savedCount}`} color="success" size="small" />
                {hasUnsavedChanges && <Chip label="Unsaved changes" color="warning" size="small" />}
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll}
                  disabled={saving || savingStudent !== null || loadingStudents || !students.length} size="small">
                  {saving ? 'Saving...' : 'Save All'}
                </Button>
              </Box>
            </Box>

            {loadingStudents ? (
              <Box py={5} textAlign="center"><CircularProgress size={28} /></Box>
            ) : filteredStudents.length === 0 ? (
              <EmptyRecordsState message={search ? 'No students match your search.' : 'No students found for the selected class.'} />
            ) : isMobile ? (
              <>
                <ResponsiveRecordList
                  items={pagedStudents}
                  renderItem={(student, index) => {
                    const globalIndex = (pagination.current_page - 1) * perPage + index;
                    return (
                      <Stack spacing={1.5}>
                        <Typography fontWeight={700}>{globalIndex + 1}. {getStudentName(student)}</Typography>
                        <Typography variant="body2" color="text.secondary">{student.registration_no || '-'}</Typography>
                        {activeCategories.map((cat) => (
                          <Box key={cat.id}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              {cat.category_name || cat.name}
                            </Typography>
                            {renderRatingSelect(student, cat)}
                            <TextField size="small" fullWidth placeholder="Remarks" sx={{ mt: 0.5 }}
                              value={conducts[student.id]?.[cat.id]?.remarks || ''}
                              onChange={(e) => setCell(student.id, cat.id, 'remarks', e.target.value)}
                              disabled={saving || savingStudent === student.id} />
                          </Box>
                        ))}
                        <Button fullWidth variant="outlined" size="small"
                          startIcon={savingStudent === student.id ? <CircularProgress size={14} /> : <SaveIcon />}
                          onClick={() => handleSaveStudent(student)}
                          disabled={saving || savingStudent !== null}>
                          {savingStudent === student.id ? 'Saving...' : 'Save'}
                        </Button>
                      </Stack>
                    );
                  }}
                />
                <TablePagination pagination={pagination} onPageChange={setPage}
                  onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} isMobile />
              </>
            ) : (
              <>
                <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                  <Table size="small" stickyHeader sx={{ minWidth: 600 + activeCategories.length * 160 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', width: 40 }}>#</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', width: 200 }}>Student</TableCell>
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', width: 100 }}>Reg No</TableCell>
                        {activeCategories.map((cat) => (
                          <TableCell key={cat.id} sx={{ bgcolor: 'grey.100', fontWeight: 'bold', minWidth: 160 }}>
                            {cat.category_name || cat.name}
                          </TableCell>
                        ))}
                        <TableCell sx={{ bgcolor: 'grey.100', fontWeight: 'bold', width: 70 }}>Save</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pagedStudents.map((student, index) => {
                        const globalIndex = (pagination.current_page - 1) * perPage + index;
                        const hasSaved = Object.values(savedConducts[student.id] || {}).some((c) => c?.saved_id);
                        return (
                          <TableRow key={student.id} hover>
                            <TableCell sx={{ py: 1 }}>{globalIndex + 1}</TableCell>
                            <TableCell sx={{ py: 1, fontWeight: 500 }}>{getStudentName(student)}</TableCell>
                            <TableCell sx={{ py: 1, color: 'text.secondary', fontSize: '0.75rem' }}>{student.registration_no || '-'}</TableCell>
                            {activeCategories.map((cat) => (
                              <TableCell key={cat.id} sx={{ py: 0.5 }}>
                                {renderRatingSelect(student, cat)}
                              </TableCell>
                            ))}
                            <TableCell sx={{ py: 0.5 }}>
                              <Button size="small" variant="outlined"
                                color={hasSaved ? 'success' : 'primary'}
                                onClick={() => handleSaveStudent(student)}
                                disabled={saving || savingStudent !== null}
                                sx={{ minWidth: 0, px: 1 }}>
                                {savingStudent === student.id ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination pagination={pagination} onPageChange={setPage}
                  onPageSizeChange={(s) => { setPerPage(s); setPage(1); }} />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {SnackbarComponent}
    </Box>
  );
}
